// 選んだカテゴリの調整（キャラの右端の列）（2026-10-21 に app.js から分けた）
import { S } from './state.js';
import { C } from './core/model.js';
import { CONFIG, OPACITY_KEYS, OPACITY_MIN, ZOOM_MAX } from './config.js';
import { catByKey, narrowUI, tallUI } from './store.js';
import { on } from './core/events.js';
import { txt } from './text.js';
import { updateSide } from './side.js';
import { $, keyOf } from './util.js';
import { opacityOf, render } from './core/render.js';
import { scheduleBox } from './partbox.js';
import { histCut } from './history.js';
import type { Category, PartState } from './types.js';
import { moveBy, resetTransform, setOpacity, toggleRev, zoomStep } from './core/ops.js';

// ---- 選んだカテゴリの調整（キャラの右端の列。使えるものだけ出す） ----
export function hasPart(cat: Category | undefined, st: PartState | undefined) { return !!(cat && st && st.parts != null && cat.NAME !== 'chara'); }
export function canOpacity(cat: Category | undefined, st: PartState | undefined) { return hasPart(cat, st) && OPACITY_KEYS.indexOf(keyOf(cat!)) >= 0; }
export function canMove(cat: Category | undefined, st: PartState | undefined) { return hasPart(cat, st) && cat!.IS_MOVE != 0; }
export function updateTools() {
  const cat = catByKey[S.selKey], st = C.state[S.selKey];
  const has = hasPart(cat, st);
  const can: Record<string, boolean> = {
    rev: has && cat.IS_REV != 0,
    move: canMove(cat, st),
    bigger: has && cat.IS_ZOOM != 0, smaller: has && cat.IS_ZOOM != 0,
    opacity: canOpacity(cat, st) && !opaRowOn(), // スマホでうすさの行を出すメーカーは、右の列には出さない
    reset: has && (cat.IS_MOVE != 0 || cat.IS_ZOOM != 0)
  };
  if (S.moveMode && !can.move) S.moveMode = false;
  if (S.opaKey && (S.opaKey !== S.selKey || !can.opacity)) S.opaKey = '';
  const scale = has ? Number(st.scale) || 1 : 1;
  const moved = has && (Number(st.tx) || Number(st.ty) || scale !== 1 || Number(st.rotate));
  let any = false;
  document.querySelectorAll<HTMLElement>('.tool').forEach((b) => {
    const t = b.dataset.tool as string;
    b.hidden = !can[t];
    if (can[t]) any = true;
    if (t === 'rev') { b.classList.toggle('on', !!(has && Number(st.rev) === 1)); b.setAttribute('aria-pressed', b.classList.contains('on')); }
    if (t === 'move') { b.classList.toggle('on', S.moveMode); b.setAttribute('aria-pressed', S.moveMode); }
    if (t === 'opacity') { b.classList.toggle('on', !!S.opaKey); b.classList.toggle('set', can.opacity && opacityOf(st) < 1); b.setAttribute('aria-expanded', !!S.opaKey); }
    // これ以上できないとき・戻すものが無いときは薄く
    if (t === 'bigger') b.classList.toggle('idle', scale >= 1 + ZOOM_MAX - 1e-9);
    if (t === 'smaller') b.classList.toggle('idle', scale <= 1 - ZOOM_MAX + 1e-9);
    if (t === 'reset') b.classList.toggle('idle', !moved);
  });
  $('tools').hidden = !any;
  showOpacity();
  updateOpacityRow();
  $('dpad').hidden = !S.moveMode;
  $('dpad').classList.toggle('vertical', !!(S.moveMode && cat.IS_MOVE == 1));
  $('stage').classList.toggle('moving', S.moveMode);
  scheduleBox();
  updateSide();
}
export function useTool(tool: string | undefined) {
  const cat = catByKey[S.selKey], st = C.state[S.selKey];
  if (!st) return;
  if (tool === 'opacity') { S.opaKey = S.opaKey ? '' : S.selKey; updateTools(); if (S.opaKey) $('opaRange').focus({ preventScroll: true }); return; }
  S.opaKey = '';
  if (tool !== 'move') histCut();
  if (tool === 'move') { S.moveMode = !S.moveMode; updateTools(); document.dispatchEvent(new CustomEvent('charatcreate:move', { detail: S.moveMode })); return; }
  if (tool === 'rev') toggleRev(cat);
  else if (tool === 'bigger') zoomStep(cat, 1);
  else if (tool === 'smaller') zoomStep(cat, -1);
  else if (tool === 'reset') resetTransform(cat);
  else render(S.selKey);
  updateTools();
}
// ---- スマホのうすさの行（設定 opacityRow、2026-10-29〜 KAWAII・KAWAII2） ----
// うすさを変えられるカテゴリを選んでいるあいだ、色の帯の下に「うすい ━●━ こい 6%」をはじめから出す（右の列の「うすさ」を押さなくていい）。
// 一覧の絵は 100% の濃さなので、いまのうすさが見えていないと「選んでも色がのらない」と迷う（はじめのメイクは 6%。ユーザーの実機の指摘 2026-10-29）。
// 全身のメーカーのスマホ（tallUI）と PC は今までどおり右の列のボタン
function opaRowOn() { return !!CONFIG.opacityRow && narrowUI.matches && !tallUI; }
let opaRowEl: HTMLElement | null = null;
function opaRow() {
  if (opaRowEl) return opaRowEl;
  const row = document.createElement('div');
  row.className = 'opa-row';
  row.id = 'opaRow';
  row.hidden = true;
  const thin = document.createElement('span'), thick = document.createElement('span'), out = document.createElement('output');
  thin.className = thick.className = 'opa-end';
  thin.textContent = txt('opacity_thin');
  thick.textContent = txt('opacity_thick');
  const range = document.createElement('input');
  range.type = 'range';
  range.min = String(OPACITY_MIN);
  range.max = '100';
  range.step = '5';
  range.setAttribute('aria-label', txt('tool_opacity'));
  row.append(thin, range, thick, out);
  range.addEventListener('pointerdown', () => { histCut(); });
  range.addEventListener('keydown', () => { histCut(); });
  range.addEventListener('input', () => {
    const cat = catByKey[S.selKey];
    if (!cat || !C.state[S.selKey]) return;
    out.textContent = range.value + '%';
    setOpacity(cat, Number(range.value));
  });
  range.addEventListener('change', updateTools);
  $('colorsFold').after(row);
  opaRowEl = row;
  return row;
}
export function updateOpacityRow() {
  if (!opaRowOn()) { if (opaRowEl) opaRowEl.hidden = true; return; }
  const cat = catByKey[S.selKey], st = C.state[S.selKey], row = opaRow();
  row.hidden = !canOpacity(cat, st);
  if (row.hidden) return;
  const v = Math.round(opacityOf(st) * 100), range = row.querySelector('input')!;
  if (document.activeElement !== range) range.value = String(Math.max(OPACITY_MIN, v));
  row.querySelector('output')!.textContent = v + '%';
}
// 元に戻す・読み込み・ランダムでうすさが変わったときも合わせる
on('drawn', (dcat) => { if (keyOf(dcat) === S.selKey) updateOpacityRow(); });
on('drawnAll', () => { updateOpacityRow(); });
export function showOpacity() {
  const pop = $('opaPop'), btn = document.querySelector('.tool[data-tool="opacity"]')!, st = C.state[S.opaKey];
  pop.hidden = !S.opaKey || !st;
  if (pop.hidden) return;
  const v = Math.round(opacityOf(st) * 100);
  $<HTMLInputElement>('opaRange').value = String(Math.max(OPACITY_MIN, v));
  $('opaVal').textContent = v + '%';
  const host = $('tools').offsetParent!, hr = host.getBoundingClientRect(), br = btn.getBoundingClientRect(), rr = $('tools').getBoundingClientRect();
  if (tallUI) { pop.style.left = Math.round(rr.right - hr.left + 8) + 'px'; pop.style.right = 'auto'; } // 全身のメーカーのスマホは道具の列が左
  else pop.style.right = Math.round(hr.right - rr.left + 8) + 'px';
  pop.style.top = Math.round(br.top + br.height / 2 - hr.top - pop.offsetHeight / 2) + 'px';
}
export function bindOpacity() {
  const range = $<HTMLInputElement>('opaRange');
  const cut = () => { histCut(); };
  range.addEventListener('pointerdown', cut);
  range.addEventListener('keydown', (e) => { if (e.key === 'Escape') { S.opaKey = ''; updateTools(); } else cut(); });
  range.addEventListener('input', () => {
    const cat = catByKey[S.opaKey], st = C.state[S.opaKey];
    if (!cat || !st) return;
    const v = Number(range.value);
    $('opaVal').textContent = v + '%';
    setOpacity(cat, v);
  });
  range.addEventListener('change', updateTools);
  document.addEventListener('pointerdown', (e) => {
    if (S.opaKey && !(e.target as Element).closest('#opaPop, .tool[data-tool="opacity"]')) { S.opaKey = ''; updateTools(); }
  }, true);
  window.addEventListener('resize', () => { if (S.opaKey) showOpacity(); });
}
// 1 ずつ動かす（IS_MOVE 1 は縦だけ、3 は縦横。反転していても画面の向きどおり）
export function nudge(dir: string | undefined) {
  const cat = catByKey[S.selKey], st = C.state[S.selKey];
  if (!canMove(cat, st)) return;
  if ((dir === 'left' || dir === 'right') && cat.IS_MOVE != 3) return;
  const d = ({ left: [-1, 0], right: [1, 0], up: [0, -1], down: [0, 1] } as Record<string, number[]>)[dir as string] || [0, 0];
  moveBy(cat, d[0], d[1]);
}
// 十字キー: 押すと 1、押したままにすると少し待ってから 0.1 秒ごとに続ける（本物の長押しと同じ間隔）
export function bindPad() {
  var wait: number | null = null, repeat: number | null = null;
  function stop() {
    clearTimeout(wait); clearInterval(repeat); wait = repeat = null;
    updateTools();
  }
  document.querySelectorAll<HTMLElement>('.pad[data-dir]').forEach((b) => {
    const dir = b.dataset.dir;
    b.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      b.setPointerCapture(e.pointerId);
      stop();
      nudge(dir);
      wait = setTimeout(() => { repeat = setInterval(() => { nudge(dir); }, 100); }, 350);
    });
    b.addEventListener('pointerup', stop);
    b.addEventListener('pointercancel', stop);
    b.addEventListener('lostpointercapture', stop);
    // キーボード（Enter・スペース）は click だけが来る
    b.addEventListener('click', (e) => { if (e.detail === 0) { nudge(dir); updateTools(); } });
  });
  $('dpadDone').addEventListener('click', () => { S.moveMode = false; updateTools(); });
  // PC は矢印キーでも（移動中だけ）
  document.addEventListener('keydown', (e) => {
    if (!S.moveMode || !$('modal').hidden) return;
    const dir = ({ ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right' } as Record<string, string>)[e.key];
    if (dir) { e.preventDefault(); nudge(dir); updateTools(); }
    if (e.key === 'Escape') { S.moveMode = false; updateTools(); }
  });
}
